import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";

export default function PartnerOnboard() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get(`/partner/onboard/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setErr(e.response?.data?.detail || "Invalid or expired link."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters.");
    if (pw !== pw2) return toast.error("Passwords don't match.");
    setBusy(true);
    try {
      const { data } = await http.post(`/partner/onboard/${token}/complete`, { password: pw });
      if (data.token) {
        localStorage.setItem("stc_token", data.token);
      } else {
        // Fallback: standard login round-trip with new password
        const { data: l } = await http.post("/auth/login", { email: info.email, password: pw });
        localStorage.setItem("stc_token", l.token);
      }
      toast.success("Welcome to the club.");
      // Hard navigate so AuthContext picks up the new token.
      window.location.assign("/partner");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't set your password.");
    } finally { setBusy(false); }
  };

  if (err) {
    return (
      <Layout>
        <section className="py-32 px-6 text-center">
          <div className="max-w-md mx-auto border border-red-700/40 bg-red-900/10 p-10">
            <h1 className="font-display text-3xl text-[#F4F1EA] mb-3">{err}</h1>
            <p className="text-[#B5B0A7] text-[14px]">If you think this is a mistake, email <a href="mailto:hello@simpletruthsclub.com" className="text-[#C9A055]">hello@simpletruthsclub.com</a>.</p>
          </div>
        </section>
      </Layout>
    );
  }
  if (!info) {
    return <Layout><div className="py-32 text-center text-[#8a857d]"><Loader2 className="animate-spin inline-block" size={16} /></div></Layout>;
  }

  return (
    <Layout>
      <section className="py-24 px-6 min-h-[80vh]">
        <div className="max-w-md mx-auto">
          <div className="inline-flex items-center justify-center w-14 h-14 border border-[#C9A055]/40 rounded-full mb-6">
            <KeyRound size={20} className="text-[#C9A055]" />
          </div>
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] uppercase mb-3">Welcome, Host</div>
          <h1 className="font-display text-4xl md:text-5xl text-[#F4F1EA] leading-[1.05]">Set your password.</h1>
          <p className="font-script text-[#C9A055] text-2xl mt-2">You&apos;re approved.</p>

          <div className="mt-6 border border-emerald-700/40 bg-emerald-900/10 p-4 flex items-center gap-3">
            <CheckCircle2 size={18} className="text-emerald-300" />
            <div className="text-[13px] text-[#E8E4DC]">
              {info.business_name} · {info.email}
            </div>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-3" data-testid="onboard-form">
            <label className="block">
              <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">Choose a password (min 8 chars)</div>
              <input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)}
                data-testid="onboard-pw" className="stc-input w-full" autoFocus autoComplete="new-password" />
            </label>
            <label className="block">
              <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">Confirm password</div>
              <input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)}
                data-testid="onboard-pw2" className="stc-input w-full" autoComplete="new-password" />
            </label>
            <button type="submit" disabled={busy || !pw || pw !== pw2}
              data-testid="onboard-submit"
              className="btn-gold w-full py-4 disabled:opacity-40 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="animate-spin" size={14} /> : "Set Password & Continue →"}
            </button>
            <p className="text-[11px] text-[#8a857d] text-center pt-2">
              You&apos;ll land on your dashboard with any pending offer ready to review.
            </p>
          </form>
        </div>
      </section>
    </Layout>
  );
}
